import type { TaskSnapshot } from "../types";

import { getUsageSummaryTotalCostUsd, isFiniteNumber } from "./currency";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type FlowCategory =
  | "plan_revision"
  | "execution"
  | "compile_recording"
  | "source_control"
  | "analysis"
  | "actor_instance";

export interface FlowCategoryConfig {
  label: string;
  badgeClass: string;
  accentShadow: string;
  iconClass: string;
  iconPath: string;
}

export interface TaskFlowGroup {
  kind: "group";
  key: string;
  category: FlowCategory;
  ticketId: string | null;
  tasks: TaskSnapshot[];
  totalCostUsd: number;
  latestUpdatedAt: number;
  statusCounts: Record<string, number>;
  repositorySlugs: string[];
  referenceRepositorySlugs: string[];
}

export interface TaskFlowSolo {
  kind: "solo";
  task: TaskSnapshot;
  latestUpdatedAt: number;
}

export type TaskFlowEntry = TaskFlowGroup | TaskFlowSolo;

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

export const FLOW_CATEGORY_CONFIG: Record<FlowCategory, FlowCategoryConfig> = {
  actor_instance: {
    accentShadow: "inset 4px 0 0 0 rgb(20 184 166)",
    badgeClass:
      "border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-500/20 dark:bg-teal-500/10 dark:text-teal-300",
    iconClass: "text-teal-500 dark:text-teal-300",
    iconPath:
      "M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18",
    label: "Actor",
  },
  analysis: {
    accentShadow: "inset 4px 0 0 0 rgb(14 165 233)",
    badgeClass:
      "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300",
    iconClass: "text-sky-500 dark:text-sky-300",
    iconPath:
      "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
    label: "Analysis",
  },
  compile_recording: {
    accentShadow: "inset 4px 0 0 0 rgb(249 115 22)",
    badgeClass:
      "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300",
    iconClass: "text-orange-500 dark:text-orange-300",
    iconPath:
      "M3 7h12a2 2 0 012 2v8a2 2 0 01-2 2H3a2 2 0 01-2-2V9a2 2 0 012-2zm16.293 2.293A1 1 0 0121 10v6a1 1 0 01-1.707.707l-2-2A1 1 0 0117 14v-2a1 1 0 01.293-.707l2-2z",
    label: "Compile Recording",
  },
  execution: {
    accentShadow: "inset 4px 0 0 0 rgb(16 185 129)",
    badgeClass:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    iconClass: "text-emerald-500 dark:text-emerald-300",
    iconPath:
      "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
    label: "Execution",
  },
  plan_revision: {
    accentShadow: "inset 4px 0 0 0 rgb(168 85 247)",
    badgeClass:
      "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-500/20 dark:bg-purple-500/10 dark:text-purple-300",
    iconClass: "text-purple-500 dark:text-purple-300",
    iconPath:
      "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
    label: "Plan Revision",
  },
  source_control: {
    accentShadow: "inset 4px 0 0 0 rgb(37 99 235)",
    badgeClass:
      "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300",
    iconClass: "text-blue-500 dark:text-blue-300",
    iconPath:
      "M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    label: "Source Control",
  },
};

const CATEGORY_PREFIXES: Record<string, FlowCategory> = {
  analysis: "analysis",
  compile_recording: "compile_recording",
  execution: "execution",
  plan_revision: "plan_revision",
  source_control: "source_control",
};

/* ------------------------------------------------------------------ */
/*  Functions                                                          */
/* ------------------------------------------------------------------ */

export function extractFlowCategory(taskType: string): FlowCategory | null {
  const [prefix] = taskType.split(".");
  return CATEGORY_PREFIXES[prefix] ?? null;
}

function getTaskCostUsd(task: TaskSnapshot): number {
  const cost =
    task.cost_usd ?? getUsageSummaryTotalCostUsd(task.usage_summary ?? null);
  return isFiniteNumber(cost) ? cost : 0;
}

function getMetadataTicketId(task: TaskSnapshot): string | null {
  const raw = task.metadata.ticket_id;
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isExecutionSetupTask(task: TaskSnapshot): boolean {
  if (task.type === "execution.setup") {
    return true;
  }
  const { flow } = task.metadata;
  if (flow && typeof flow === "object" && !Array.isArray(flow)) {
    const kind =
      "kind" in flow && typeof flow.kind === "string" ? flow.kind : null;
    return kind === "execution.setup.v1";
  }
  return false;
}

function getTicketCandidate(
  task: TaskSnapshot,
  category: FlowCategory | null,
): { ticketId: string; priority: number } | null {
  const linked =
    typeof task.linked_ticket_id === "string" &&
    task.linked_ticket_id.trim().length > 0
      ? task.linked_ticket_id.trim()
      : null;
  const metadataTicket = getMetadataTicketId(task);

  if (category === "execution" && isExecutionSetupTask(task)) {
    const ticketId = linked ?? metadataTicket;
    if (ticketId) {
      return { priority: 3, ticketId };
    }
  }

  const ticketId = linked ?? metadataTicket;
  if (ticketId) {
    return { priority: 1, ticketId };
  }

  return null;
}

function collectRepoSlugs(
  repoSets: { primary: Set<string>; reference: Set<string> },
  task: TaskSnapshot,
): void {
  for (const slug of task.repository_slugs ?? []) {
    repoSets.primary.add(slug);
  }
  for (const slug of task.reference_repository_slugs ?? []) {
    repoSets.reference.add(slug);
  }
}

function finalizeRepoSlugs(repoSets: {
  primary: Set<string>;
  reference: Set<string>;
}): { repositorySlugs: string[]; referenceRepositorySlugs: string[] } {
  // Remove any reference slugs that are also primary
  for (const slug of repoSets.primary) {
    repoSets.reference.delete(slug);
  }
  return {
    referenceRepositorySlugs: [...repoSets.reference].toSorted(),
    repositorySlugs: [...repoSets.primary].toSorted(),
  };
}

function extractActorInstanceId(task: TaskSnapshot): string | null {
  const meta = task.metadata;
  if (
    meta &&
    typeof meta === "object" &&
    !Array.isArray(meta) &&
    (meta as Record<string, unknown>).in_memory === true
  ) {
    const instanceId = (meta as Record<string, unknown>).instance_id;
    return typeof instanceId === "string" && instanceId.length > 0
      ? instanceId
      : null;
  }
  return null;
}

export function groupTasksByFlow(tasks: TaskSnapshot[]): TaskFlowEntry[] {
  const groupMap = new Map<string, TaskFlowGroup>();
  const ticketPriorityByRunId = new Map<string, number>();
  const repoSetsMap = new Map<
    string,
    { primary: Set<string>; reference: Set<string> }
  >();
  const solos: TaskFlowSolo[] = [];

  for (const task of tasks) {
    // Group actor instance tasks by instance_id instead of flow_run_id.
    const actorInstanceId = extractActorInstanceId(task);
    if (actorInstanceId) {
      const key = `actor:${actorInstanceId}`;
      const existing = groupMap.get(key);
      if (existing) {
        existing.tasks.push(task);
        existing.totalCostUsd += getTaskCostUsd(task);
        existing.latestUpdatedAt = Math.max(
          existing.latestUpdatedAt,
          task.updated_at,
        );
        existing.statusCounts[task.status] =
          (existing.statusCounts[task.status] ?? 0) + 1;
      } else {
        groupMap.set(key, {
          category: "actor_instance",
          key,
          kind: "group",
          latestUpdatedAt: task.updated_at,
          referenceRepositorySlugs: [],
          repositorySlugs: [],
          statusCounts: { [task.status]: 1 },
          tasks: [task],
          ticketId: actorInstanceId.slice(0, 8),
          totalCostUsd: getTaskCostUsd(task),
        });
      }
      continue;
    }

    const runId = task.flow_run_id;

    if (!runId) {
      solos.push({ kind: "solo", latestUpdatedAt: task.updated_at, task });
      continue;
    }

    const existing = groupMap.get(runId);
    if (existing) {
      existing.tasks.push(task);
      existing.totalCostUsd += getTaskCostUsd(task);
      existing.latestUpdatedAt = Math.max(
        existing.latestUpdatedAt,
        task.updated_at,
      );
      existing.statusCounts[task.status] =
        (existing.statusCounts[task.status] ?? 0) + 1;
      const candidate = getTicketCandidate(task, existing.category);
      if (candidate) {
        const currentPriority = ticketPriorityByRunId.get(runId) ?? 0;
        if (candidate.priority > currentPriority) {
          existing.ticketId = candidate.ticketId;
          ticketPriorityByRunId.set(runId, candidate.priority);
        }
      }
      collectRepoSlugs(repoSetsMap.get(runId)!, task);
    } else {
      const category = extractFlowCategory(task.type);
      const candidate = getTicketCandidate(task, category);
      const repoSets = {
        primary: new Set<string>(),
        reference: new Set<string>(),
      };
      collectRepoSlugs(repoSets, task);
      repoSetsMap.set(runId, repoSets);
      groupMap.set(runId, {
        category: category ?? "execution",
        key: runId,
        kind: "group",
        latestUpdatedAt: task.updated_at,
        referenceRepositorySlugs: [],
        repositorySlugs: [],
        statusCounts: { [task.status]: 1 },
        tasks: [task],
        ticketId: candidate?.ticketId ?? null,
        totalCostUsd: getTaskCostUsd(task),
      });
      ticketPriorityByRunId.set(runId, candidate?.priority ?? 0);
    }
  }

  // Finalize repo slugs for each group
  for (const [runId, group] of groupMap) {
    const repoSets = repoSetsMap.get(runId)!;
    const finalized = finalizeRepoSlugs(repoSets);
    group.repositorySlugs = finalized.repositorySlugs;
    group.referenceRepositorySlugs = finalized.referenceRepositorySlugs;
  }

  // Combine groups and solos, sort by latestUpdatedAt desc
  const entries: TaskFlowEntry[] = [...groupMap.values(), ...solos];

  entries.sort((a, b) => b.latestUpdatedAt - a.latestUpdatedAt);

  return entries;
}
